import {
  defaultGenericElement,
  reversionElement,
  updateElement,
} from '../../lib/element';
import {
  prepareElementForExport,
  roundExportVal,
} from '../../lib/exportImport';
import { type Plugin } from '../../lib/plugin';
import { renderStaticScene } from '../../lib/scene';
import { useEditorStore } from '../../stores/useEditorStore';
import { hexToRgbString } from '../../utils/color';
import { type Point } from '../../utils/math';
import { type BooleanElementGroupType } from '../boolean';
import {
  freedraw,
  type StrokeCap,
  type GenericFreedrawElement,
} from '../freedraw';
import { createFreedrawEvents } from '../freedraw/events';
import { getFreedrawShape } from '../freedraw/utils/shape';
import { general } from '../general';

export interface FreedrawEraserElement extends GenericFreedrawElement {
  type: 'eraser';
}

const eraserState: {
  newStrokeColor: string;
  newStrokeWidth: number;
  newStrokeCap: StrokeCap;
  booleanMode: BooleanElementGroupType;
  lastPoint: Point | null;
  lastDrawnElement: FreedrawEraserElement | null;
} = {
  newStrokeColor: hexToRgbString('#ffffff', 0.5),
  newStrokeWidth: 16,
  newStrokeCap: 'round',
  booleanMode: 'union',
  lastPoint: null,
  lastDrawnElement: null,
};

const eraserActions = {
  activateEraser: () => {
    general.actions.activate(plugin.name, true);
    eraserState.booleanMode = 'subtract';
  },
  setStrokeColor: (color: string) => {
    eraserState.newStrokeColor = hexToRgbString(color, 0.8);
  },
  setStrokeWidth: (width: number) => {
    eraserState.newStrokeWidth = width;
  },
  setStrokeCap: (cap: StrokeCap) => {
    eraserState.newStrokeCap = cap;
  },
  updateColorForElement: (id: string, color: string) => {
    const elements = useEditorStore.getState().elements;
    const element = elements.get(id) as FreedrawEraserElement;

    updateElement<FreedrawEraserElement>(elements, element, {
      strokeColor: color,
      ...reversionElement(element, ['canvas']),
    });

    renderStaticScene();
  },
};

const plugin: Plugin<
  FreedrawEraserElement,
  typeof eraserActions,
  typeof eraserState
> = {
  name: 'eraser',
  cursor: 'default',
  actions: eraserActions,
  state: eraserState,
  events: {
    ...createFreedrawEvents<FreedrawEraserElement>({
      toolName: 'eraser',
      elementType: 'eraser',
      state: eraserState,
    }),
    blockTouchPanning: true,
  },
  render: freedraw.render,
  selection: freedraw.selection,
  collision: freedraw.collision,
  // @ts-expect-error FIXME: Should be fixed in the future
  transformation: freedraw.transformation,
  export: (element) => {
    const prepared = prepareElementForExport(
      element,
    ) as Partial<FreedrawEraserElement>;

    prepared.points = element.points.map(([x, y]) => [
      roundExportVal(x),
      roundExportVal(y),
    ]);

    return prepared;
  },
  import: (element) => {
    const imported = {
      ...defaultGenericElement(),
      ...element,
    } as FreedrawEraserElement;

    imported.shape = getFreedrawShape(imported);

    return imported;
  },
};

export const eraser = plugin;
